import { AnimatePresence } from "framer-motion";
import { Settings2Icon, ShareIcon } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import type { SharedSession } from "@/queries/prompts";

const MANAGE_TRIGGER_CLASS = "gap-1.5 bg-blue-600 text-white shadow-sm hover:bg-blue-700";

export interface ShareSessionDialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasExistingSessions: boolean | undefined;
  isCheckingExistence: boolean;
  sessions: SharedSession[] | undefined;
  triggerSize: "sm" | "xs";
  triggerAriaLabel: string;
  hideTrigger?: boolean;
  children: ReactNode;
}

export const ShareSessionDialogShell = ({
  open,
  onOpenChange,
  hasExistingSessions,
  isCheckingExistence,
  sessions,
  triggerSize,
  triggerAriaLabel,
  hideTrigger = false,
  children,
}: ShareSessionDialogShellProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    {!hideTrigger && (
      <DialogTrigger asChild>
        <Button
          variant={hasExistingSessions === true ? "default" : "outline"}
          size={triggerSize}
          aria-label={triggerAriaLabel}
          disabled={isCheckingExistence}
          className={hasExistingSessions === true ? MANAGE_TRIGGER_CLASS : "gap-1.5"}
        >
          {hasExistingSessions === true ? (
            <Settings2Icon className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ShareIcon className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {hasExistingSessions === true ? "Manage Sharing" : "Share"}
          {hasExistingSessions === true && sessions && sessions.length > 1 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-semibold leading-none text-white ring-1 ring-blue-400/60">
              {sessions.length}
            </span>
          )}
        </Button>
      </DialogTrigger>
    )}

    <DialogContent
      className="!gap-0 overflow-hidden p-0 sm:max-w-md sm:rounded-xl"
      shouldShowCloseButton={false}
    >
      <AnimatePresence mode="wait" initial={false}>
        {children}
      </AnimatePresence>
    </DialogContent>
  </Dialog>
);
